import React, { useMemo, useState } from "react";
import { Bookmark, BookmarkCheck, ExternalLink, Lightbulb, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CopyButton from "./CopyButton";
import ToolCard from "./ToolCard";
import { TOOL_BY_ID } from "@/data/ai-lab/aiTools";
import { eventsApi, savedPromptsApi } from "@/lib/ai-lab/api";

/** Tools that accept a prefilled prompt via URL (kept short to avoid URL length limits). */
const PREFILL = {
  chatgpt: (p) => `https://chatgpt.com/?q=${encodeURIComponent(p)}`,
  "chatgpt-images": (p) => `https://chatgpt.com/?q=${encodeURIComponent(p)}`,
  claude: (p) => `https://claude.ai/new?q=${encodeURIComponent(p)}`,
  perplexity: (p) => `https://www.perplexity.ai/search?q=${encodeURIComponent(p)}`,
  "perplexity-research": (p) => `https://www.perplexity.ai/search?q=${encodeURIComponent(p)}`,
};
const PREFILL_MAX = 6000;

/**
 * PromptOutput — the right-hand result panel of the Prompt Builder.
 *
 * props
 *   task, prompt (string), formValues (for saving), onReset
 */
export default function PromptOutput({ task, prompt, formValues, onReset }) {
  const [edited, setEdited] = useState(null); // user edits to the generated prompt
  const [saved, setSaved] = useState(false);
  const text = edited ?? prompt;
  const tools = useMemo(() => (task.recommendedTools || []).map((id) => TOOL_BY_ID[id]).filter(Boolean), [task]);
  const primary = tools[0];
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const copyAndOpen = async (tool) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore — user can still paste from the Copy button */
    }
    const canPrefill = PREFILL[tool.id] && text.length <= PREFILL_MAX;
    const url = canPrefill ? PREFILL[tool.id](text) : tool.url;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(canPrefill ? `Opening ${tool.name} with your prompt.` : `Prompt copied — paste it into ${tool.name}.`);
    eventsApi.track({ type: "open_tool", taskId: task.id, toolId: tool.id, context: "prompt-builder" });
  };

  const save = async () => {
    try {
      await savedPromptsApi.create({ taskId: task.id, title: task.title, category: task.category, prompt: text, inputs: formValues });
      setSaved(true);
      toast.success("Saved to My Prompts.");
    } catch {
      toast.error("Couldn't save right now. Copy the prompt to keep it.");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Your prompt</CardTitle>
            <p className="text-xs text-muted-foreground">{words} words · edit anything below before copying</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <CopyButton text={() => text} onCopied={() => eventsApi.track({ type: "copy_prompt", taskId: task.id, context: "prompt-builder" })} />
            <Button variant="outline" size="default" onClick={save} disabled={saved}>
              {saved ? <BookmarkCheck className="mr-2 h-4 w-4" /> : <Bookmark className="mr-2 h-4 w-4" />}
              {saved ? "Saved" : "Save"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setEdited(e.target.value)}
            rows={18}
            spellCheck={false}
            className="min-h-[320px] resize-y font-mono text-[13px] leading-relaxed"
          />
          <div className="flex flex-wrap items-center gap-2">
            {edited !== null && (
              <Button variant="ghost" size="sm" onClick={() => setEdited(null)}><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Undo my edits</Button>
            )}
            {onReset && <Button variant="ghost" size="sm" onClick={onReset}>Change answers</Button>}
          </div>
        </CardContent>
      </Card>

      {primary && (
        <Card className="border-pink-300 dark:border-pink-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              Next step: paste it into {primary.name}
              <Badge className="bg-pink-500 text-white hover:bg-pink-500">Recommended</Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">{primary.why}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {tools.map((tool) => (
                <Button key={tool.id} variant={tool.id === primary.id ? "default" : "outline"} onClick={() => copyAndOpen(tool)}>
                  Open in {tool.name} <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              ))}
            </div>
            {task.outputType !== "text" && (
              <p className="text-xs text-muted-foreground">
                This is {task.outputType === "image" ? "an image" : task.outputType === "video" ? "a video" : "an audio"}-generation prompt. Upload your product photo or logo to the tool first, then paste.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {tools.length > 1 && (
        <div className="grid gap-2">
          {tools.slice(1).map((tool) => <ToolCard key={tool.id} tool={tool} compact recommended={false} context="prompt-builder" />)}
        </div>
      )}

      {task.tips?.length > 0 && (
        <Card className="bg-muted/40">
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Lightbulb className="h-4 w-4 text-pink-500" />Pro tips</div>
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
              {task.tips.map((tip, i) => <li key={i}>{tip}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
